<template>
  <div class="advice2-wrap">
    <main class="screen" role="main">

      <div class="header">
        <button
          type="button"
          class="back-button"
          @click="goBack"
          aria-label="Zurück"
        >←</button>
      </div>

      <span class="badge">Dein 30-Tage-Plan</span>
      <h1 class="title">Vier kleine Veränderungen</h1>
      <p class="subtitle">Basierend auf deinem Quiz-Ergebnis. Hak ab, was du diesen Monat ausprobierst.</p>

      <div class="progress-card">
        <div>
          <p class="progress-label">Diesen Monat</p>
          <p class="progress-value"><strong>{{ doneCount }}</strong> von {{ actions.length }} erledigt</p>
        </div>
        <div class="progress-ring" :aria-label="`${donePct} Prozent erledigt`">
          <svg viewBox="0 0 60 60">
            <circle class="ring-track" cx="30" cy="30" r="25"/>
            <circle
              class="ring-fill"
              cx="30"
              cy="30"
              r="25"
              :stroke-dashoffset="ringOffset"
            />
          </svg>
          <div class="ring-text">{{ donePct }}%</div>
        </div>
      </div>

      <h2 class="section-heading">Deine Aktionen</h2>

      <div class="action-list">
        <button
          v-for="action in actions"
          :key="action.id"
          type="button"
          class="action-item"
          :class="{ done: action.done }"
          @click="toggle(action)"
          :aria-pressed="action.done"
        >
          <span class="action-checkbox">{{ action.done ? '✓' : '' }}</span>
          <span class="action-content">
            <span class="action-headline">{{ action.headline }}</span>
            <span class="action-detail">{{ action.detail }}</span>
            <span class="action-tag">{{ action.tag }}</span>
          </span>
        </button>
      </div>

      <div class="reminder-card">
        <span aria-hidden="true">📅</span>
        <span>In 3 Monaten erinnern wir dich, den Quiz erneut auszufüllen. Du kannst dann sehen, wie sich dein Ergebnis verändert hat.</span>
      </div>

      <p class="placeholder-note">Beispieltexte. Die finalen Inhalte werden vom Ernährungsteam erstellt.</p>

    </main>
  </div>
</template>

<script>
const RING_CIRCUMFERENCE = 157;

export default {
  name: 'Advice2',
  data() {
    return {
      actions: [
        { id: 'huelsen', headline: 'Hülsenfrüchte 2× pro Woche', detail: 'Linsensalat, Hummus oder ein Kichererbsen-Curry.', tag: '🌱 Pflanzliche Proteine', done: true },
        { id: 'vollkorn', headline: 'Vollkornbrot statt Weissbrot', detail: 'Beim nächsten Einkauf umstellen.', tag: '🌾 Getreide', done: true },
        { id: 'fisch', headline: '1× pro Woche Fisch', detail: 'Frisch, geräuchert oder aus der Konserve.', tag: '🥩 Tierische Proteine', done: false },
        { id: 'naturejoghurt', headline: 'Naturejoghurt statt Fruchtjoghurt', detail: 'Mit frischen Früchten dazu.', tag: '🥛 Milchprodukte', done: false },
      ],
    };
  },
  computed: {
    doneCount() {
      return this.actions.filter(a => a.done).length;
    },
    donePct() {
      return Math.round((this.doneCount / this.actions.length) * 100);
    },
    ringOffset() {
      return RING_CIRCUMFERENCE * (1 - this.doneCount / this.actions.length);
    },
  },
  methods: {
    toggle(action) {
      action.done = !action.done;
    },
    goBack() { this.$router.back(); },
  },
};
</script>

<style scoped>
.advice2-wrap {
  --color-bg-page: #f5f4ef;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f1efe8;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.15);
  --color-accent: #534AB7;
  --color-accent-light: #EEEDFE;
  --color-good: #639922;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.5;
  min-height: 100%;
  padding: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.advice2-wrap *,
.advice2-wrap *::before,
.advice2-wrap *::after {
  box-sizing: border-box;
}

.screen {
  width: 100%;
  max-width: 420px;
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem 1.25rem;
  min-height: 600px;
}

.header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.back-button {
  background: transparent;
  border: none;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 20px;
  color: var(--color-text-secondary);
  line-height: 1;
}

.badge {
  display: inline-block;
  background: var(--color-accent-light);
  color: var(--color-accent);
  font-size: 12px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: var(--radius-md);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.title {
  font-size: 24px;
  font-weight: 500;
  margin: 0 0 8px;
  line-height: 1.25;
}

.subtitle {
  font-size: 15px;
  color: var(--color-text-secondary);
  margin: 0 0 1.5rem;
}

.progress-card {
  background: var(--color-bg-muted);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.progress-label {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0 0 4px;
}

.progress-value {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

.progress-value strong {
  color: var(--color-accent);
}

.progress-ring {
  width: 56px;
  height: 56px;
  position: relative;
}

.progress-ring svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.ring-track { fill: none; stroke: var(--color-border); stroke-width: 5; }

.ring-fill {
  fill: none;
  stroke: var(--color-accent);
  stroke-width: 5;
  stroke-linecap: round;
  stroke-dasharray: 157;
  transition: stroke-dashoffset 0.4s ease-out;
}

.ring-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 500;
}

.section-heading {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 12px;
}

.action-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 1.5rem;
}

.action-item {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  font-family: inherit;
  text-align: left;
  width: 100%;
}

.action-item:hover {
  background: var(--color-bg-muted);
}

.action-item.done {
  background: var(--color-bg-muted);
  border-color: var(--color-good);
}

.action-checkbox {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1.5px solid var(--color-border);
  background: var(--color-bg-card);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: white;
  margin-top: 1px;
}

.action-item.done .action-checkbox {
  background: var(--color-good);
  border-color: var(--color-good);
}

.action-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.action-headline {
  font-size: 14px;
  font-weight: 500;
  margin: 0 0 4px;
  color: var(--color-text-primary);
}

.action-item.done .action-headline {
  color: var(--color-text-tertiary);
  text-decoration: line-through;
}

.action-detail {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.4;
}

.action-tag {
  display: inline-block;
  align-self: flex-start;
  font-size: 11px;
  color: var(--color-accent);
  background: var(--color-accent-light);
  padding: 2px 6px;
  border-radius: 4px;
  margin-top: 6px;
}

.reminder-card {
  background: var(--color-accent-light);
  border-radius: var(--radius-md);
  padding: 14px 16px;
  font-size: 13px;
  color: var(--color-accent);
  line-height: 1.5;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.placeholder-note {
  font-size: 11px;
  color: var(--color-text-tertiary);
  text-align: center;
  margin-top: 1rem;
  font-style: italic;
}
</style>
